import type { CampaignWindow, FlashSaleRotation } from '../../utils/campaign/flashSaleRotation';

export type CampaignPromoBackground = 'black-gold' | 'purple';

export type CampaignPromoTextPart = {
  text: string;
  bold?: boolean;
};

/** Topbar flag copy. Omit on a campaign that should not take over the flag. */
export type CampaignPromo = {
  textParts: CampaignPromoTextPart[];
  background: CampaignPromoBackground;
};

/** Copy for a campaign's flash-sale block. */
export type CampaignFlashSaleCopy = {
  /** The section heading, linking to the campaign page. */
  sectionTitle: string;
  /**
   * Joined with a space into the single promo line under the product rail — SSD-6744 removed the
   * hero banner these two were written for. That strip does not wrap, so the joined sentence has
   * to stay under roughly 45 characters or it overflows on a 360px screen.
   */
  bannerTitle: string;
  bannerSubtitle: string;
  /** The page-top announcement bar, above the flash-sale block. */
  textBannerCopy: string;
};

/** One window of a daily rotation. The url is the parent campaign's, so it is not repeated here. */
export type CampaignFlashSaleWindow = CampaignWindow & {
  /** The exact WordPress term name: `GET /products/collection` matches on the name, not the slug. */
  collectionName: string;
  collectionSlug: string;
};

/** Rotating flash-sale block. Omit on a campaign that does not run one. */
export type CampaignFlashSale = {
  copy: CampaignFlashSaleCopy;
  /** Daily windows in order. Gaps are allowed; the block simply goes dark between them. */
  rotations: CampaignFlashSaleWindow[];
  /** Also swap the PDP "Same Category" module for this rotation while it is live. */
  showOnPdp?: boolean;
};

/** A flash-sale window flattened for a surface — its campaign's url and copy stamped on. */
export type CampaignFlashSaleRotation = FlashSaleRotation & {
  collectionSlug: string;
  copy: CampaignFlashSaleCopy;
};

export type Campaign = CampaignWindow & {
  /** Human label so the config reads like the campaign calendar. Not rendered. */
  name: string;
  /** Where the flag sends the shopper. */
  campaignUrl: string;
  promo?: CampaignPromo;
  flashSale?: CampaignFlashSale;
};

/**
 * The one campaign calendar. Both the topbar flag (`HOLIDAY_BANNER_ROTATIONS`) and the homepage
 * flash-sale rail (`FLASH_SALE_ROTATIONS`) are derived from this list, so a campaign is scheduled
 * once and every surface follows the same window.
 *
 * All times are SGT (`Asia/Singapore`, fixed UTC+8, no DST), format 'YYYY-MM-DD HH:mm:ss'.
 *
 * **Order is significant.** Windows may overlap and the first match wins, so a campaign that
 * should take priority is listed above the one it overlaps.
 *
 * Past campaigns are deleted once they end rather than kept for history — git has that, and a
 * stale window can only add noise. The topbar falls back to DEFAULT_HOLIDAY_BANNER when the list
 * matches nothing, and the flash-sale rail simply goes dark.
 *
 * Keep the promo half in lockstep with astro-blog `src/data/promo.ts`, which carries its own
 * copy of the same schedule for the blog topbar.
 */
export const CAMPAIGNS: Campaign[] = [
  {
    name: 'The K-Beauty Drop',
    startDate: '2026-10-05 15:00:00',
    endDate: '2026-10-12 15:00:00',
    campaignUrl: '/campaign/the-k-beauty-drop/',
    // No promo: the top flag stays on BAU free shipping outside the Prime Day entry below.
    flashSale: {
      // SSD-6910: replaces the PDP "Same Category" module for the whole campaign.
      showOnPdp: true,
      copy: {
        sectionTitle: 'Daily Flash Drop',
        bannerTitle: 'New deals every 24h,',
        bannerSubtitle: 'Grab your favorites fast.',
        textBannerCopy: 'Up to 50% off across the selection',
      },
      // Seven collections, one live per day from 3PM SGT to 3PM SGT. Merch swaps the sale prices in
      // WordPress on the same boundary, so a handover needs no deploy. The leading "The" is part of
      // the term name and slug — "k-beauty-drop-flash-sale-N" does not exist.
      rotations: [
        {
          startDate: '2026-10-05 15:00:00',
          endDate: '2026-10-06 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 1',
          collectionSlug: 'the-k-beauty-drop-flash-sale-1',
        },
        {
          startDate: '2026-10-06 15:00:00',
          endDate: '2026-10-07 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 2',
          collectionSlug: 'the-k-beauty-drop-flash-sale-2',
        },
        {
          startDate: '2026-10-07 15:00:00',
          endDate: '2026-10-08 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 3',
          collectionSlug: 'the-k-beauty-drop-flash-sale-3',
        },
        {
          startDate: '2026-10-08 15:00:00',
          endDate: '2026-10-09 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 4',
          collectionSlug: 'the-k-beauty-drop-flash-sale-4',
        },
        {
          startDate: '2026-10-09 15:00:00',
          endDate: '2026-10-10 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 5',
          collectionSlug: 'the-k-beauty-drop-flash-sale-5',
        },
        {
          startDate: '2026-10-10 15:00:00',
          endDate: '2026-10-11 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 6',
          collectionSlug: 'the-k-beauty-drop-flash-sale-6',
        },
        {
          startDate: '2026-10-11 15:00:00',
          endDate: '2026-10-12 15:00:00',
          collectionName: 'The K-Beauty Drop Flash Sale 7',
          collectionSlug: 'the-k-beauty-drop-flash-sale-7',
        },
      ],
    },
  },
  {
    // SSD-7026: the flag runs on its own window inside the campaign, so it is a separate promo-only
    // entry. Kept below the flash-sale entry, which lookups by campaignUrl expect first. SSD-7124 cut
    // it from two days to one; the flag then reverts to BAU free shipping for good.
    name: 'The K-Beauty Drop: Prime Day',
    startDate: '2026-10-06 15:00:00',
    endDate: '2026-10-07 15:00:00',
    campaignUrl: '/campaign/the-k-beauty-drop/',
    promo: {
      textParts: [
        { text: 'Use code ' },
        { text: 'DROP10', bold: true },
        { text: ' at checkout for ' },
        { text: 'EXTRA 10% OFF', bold: true },
      ],
      background: 'purple',
    },
  },
];

export const FLASH_SALE_TIMEZONE = 'Asia/Singapore';

type CampaignWithFlashSale = Campaign & { flashSale: NonNullable<Campaign['flashSale']> };

const hasFlashSale = (campaign: Campaign): campaign is CampaignWithFlashSale => Boolean(campaign.flashSale);

const toFlashSaleRotations = (campaigns: CampaignWithFlashSale[]): CampaignFlashSaleRotation[] =>
  campaigns.flatMap((campaign) =>
    campaign.flashSale.rotations.map((rotation) => ({
      ...rotation,
      campaignUrl: campaign.campaignUrl,
      copy: campaign.flashSale.copy,
    })),
  );

/**
 * Homepage flash-sale schedule (SSD-6696), derived from the one campaign calendar in `campaigns.ts`.
 *
 * Every campaign running a daily rotation contributes its windows in registry order, each stamped
 * with the parent campaign's url and copy. Carrying the copy on the window — rather than exporting
 * it separately — is what lets a surface resolve the collection and the wording it ships with in the
 * same lookup, so the two cannot drift once a second campaign runs a rotation of its own.
 *
 * Schedule a flash sale in `CAMPAIGNS`, not here: that is what keeps it on the same window as the
 * topbar flag.
 */
export const FLASH_SALE_ROTATIONS: CampaignFlashSaleRotation[] = toFlashSaleRotations(CAMPAIGNS.filter(hasFlashSale));

/**
 * The PDP's flash-sale schedule (SSD-6004): the rotations of campaigns that opt in with
 * `showOnPdp`. While one is live the PDP shows it in place of "Same Category", and falls back to
 * "Same Category" between and after them.
 */
export const PDP_FLASH_SALE_ROTATIONS: CampaignFlashSaleRotation[] = toFlashSaleRotations(
  CAMPAIGNS.filter(hasFlashSale).filter((campaign) => campaign.flashSale.showOnPdp),
);
